import cn from 'classnames';
import React, { memo, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Icon } from 'components';
import BannerUpload from 'components/BannerUpload';
import IconButton from 'components/IconButton';

import Gamification, { GamificationProps } from '../Gamification';

import styles from './CoverImage.module.scss';

export interface CoverImageProps extends GamificationProps {
  banner: string;
  onDeleteCoverPhoto: () => Promise<void>;
  showProgressBanner: boolean;
}

export const CoverImage: React.FC<CoverImageProps> = memo(
  ({
    banner,
    onDeleteCoverPhoto,
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    showProgressBanner,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
  }: CoverImageProps) => {
    const { register, watch, setValue } = useFormContext(); // retrieve all hook methods
    const bannerImageFiles = watch('banner');
    const bannerRemoved = watch('remove_banner');

    const [showProgressBannerState, setShowProgressBannerState] =
      React.useState<boolean>(showProgressBanner);

    useEffect(() => {
      if (showProgressBannerState === false)
        setShowProgressBannerState(showProgressBanner);
    }, [showProgressBanner]);

    useEffect(() => {
      register('banner');
    }, [register]);

    const [showFileSizeError, setShowFileSizeError] = useState<boolean>(false);

    const onChangeBanner = (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const files: FileList = target.files as FileList;
      if (files[0].size / 1000 < 5001) {
        setShowFileSizeError(false);
        setValue('banner', files, { shouldDirty: true });
        setValue('remove_banner', false, { shouldDirty: true });
      } else {
        setShowFileSizeError(true);
      }
    };

    const bannerImage =
      typeof bannerImageFiles === 'object' && bannerImageFiles?.length > 0
        ? URL.createObjectURL(bannerImageFiles[0])
        : bannerImageFiles && typeof bannerImageFiles === 'string'
        ? bannerImageFiles
        : bannerRemoved
        ? ''
        : banner;

    const defaultImage = '/images/upload-image-2.png';
    const availableImage = bannerImage || defaultImage;
    const isDefaultImage = availableImage === defaultImage;

    return (
      <div id={`section_${progressDetails?.id}`}>
        {showProgressBannerState && (
          <Gamification
            profileCompletePercent={profileCompletePercent}
            nonCompletedSection={nonCompletedSection}
            progressDetails={progressDetails}
            handleProfileCompletionButton={() => {
              setShowProgressBannerState(false);
              handleProfileCompletionButton();
            }}
            handleNextButton={() => {
              setShowProgressBannerState(false);
              handleNextButton && handleNextButton();
            }}
            showNextButton={showNextButton}
          />
        )}
        <div data-testid="CoverImageProps">
          <div
            className={cn(styles.bannerMain, {
              [styles.bannerMainDefault]: isDefaultImage,
            })}
          >
            <img
              src={availableImage}
              className={cn({
                [styles.banner]: !isDefaultImage,
                [styles.bannerDefault]: isDefaultImage,
                [styles.noBorderRadius]: showProgressBanner,
              })}
            />
            {isDefaultImage ? (
              <p className={styles.bannerMainTitle}>Cover Photo</p>
            ) : null}

            <div
              className={cn(styles.bannerWrapper, {
                [styles.bannerWrapperDefault]: isDefaultImage,
              })}
            >
              <BannerUpload
                image={availableImage}
                color="white"
                buttonClassName={styles.bannerUploadButton}
                className={cn({ [styles.bannerUpload]: !isDefaultImage })}
                alt={'Company banner image'}
                title={bannerImage ? 'Change Cover photo' : 'Add Cover photo'}
                onChange={onChangeBanner}
              />
              {bannerImage && (
                <IconButton
                  size="small"
                  iconName="trash"
                  className={styles.trashIcon}
                  onClick={() => {
                    setValue('banner', '', { shouldDirty: true });
                    onDeleteCoverPhoto();
                  }}
                  type="button"
                />
              )}
            </div>
            {showFileSizeError ? (
              <div
                className={cn(styles['error-container'], {
                  [styles['has-banner-image']]: bannerImage,
                })}
              >
                <Icon iconName={'info'}></Icon>
                <span>
                  Please upload an image with a size of less than 5 MB
                </span>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    );
  }
);

CoverImage.displayName = 'CoverImage';
